/**
 * BookContext - Project-scoped book/chapter state management
 *
 * Provides books, chapters, and chapter workspace state for the active project.
 * Wraps inside ProjectProvider. When activeProjectId changes, refreshes books.
 *
 * @module @humanizer/studio/contexts/BookContext
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useProject } from './ProjectContext';
import type {
  BookData,
  ChapterData,
  ChapterHydration,
  OutlineStateDataElectron,
  BookPlanChapter,
} from '../types/electron';
import { createLogger } from '../utils/debug';

const log = createLogger('BookContext');

interface BookContextValue {
  // Books
  books: BookData[];
  activeBook: BookData | null;
  activeBookId: string | null;
  setActiveBook: (id: string | null) => void;
  createBook: (title: string, description?: string) => Promise<BookData | null>;
  updateBook: (id: string, update: { title?: string; description?: string }) => Promise<void>;
  deleteBook: (id: string) => Promise<void>;
  refreshBooks: () => Promise<void>;

  // Chapters
  chapters: ChapterData[];
  activeChapter: ChapterData | null;
  activeChapterId: string | null;
  setActiveChapter: (id: string | null) => void;
  createChapter: (title: string, bookId?: string) => Promise<ChapterData | null>;
  updateChapter: (id: string, update: { title?: string; content?: string; position?: number; wordCount?: number; metadata?: Record<string, unknown> }) => Promise<void>;
  deleteChapter: (id: string) => Promise<void>;

  // Book planning (Phase 1)
  planAndCreateBook: (plan: {
    title: string;
    description?: string;
    chapters: BookPlanChapter[];
    cardBufferIds: string[];
  }) => Promise<BookData | null>;

  addChaptersToBook: (bookId: string, plan: {
    chapters: BookPlanChapter[];
    cardBufferIds: string[];
  }) => Promise<BookData | null>;

  stashBook: (bookId: string) => Promise<BookData | null>;

  // Chapter reorder
  reorderChapters: (orderedChapterIds: string[]) => Promise<void>;

  // Chapter workspace
  chapterCardIds: string[];
  hydrateChapter: (chapterId: string) => Promise<ChapterHydration | null>;
  saveChapterOutline: (state: OutlineStateDataElectron | null) => Promise<void>;
  saveChapterDraftBuffer: (draftBufferId: string | null, chapterId?: string) => Promise<void>;
  addCardToChapter: (bufferId: string, sortOrder?: number, chapterId?: string) => Promise<void>;
  removeCardFromChapter: (bufferId: string) => Promise<void>;
  reorderChapterCards: (orderedBufferIds: string[]) => Promise<void>;

  // Batch draft generation
  batchGenerating: boolean;
  batchProgress: { current: number; total: number; chapterTitle: string } | null;
  generateAllDrafts: (bookId: string) => Promise<void>;
  cancelBatchGeneration: () => void;
}

const BookContext = createContext<BookContextValue | null>(null);

export function BookProvider({ children }: { children: React.ReactNode }) {
  const { activeProjectId } = useProject();

  const [books, setBooks] = useState<BookData[]>([]);
  const [activeBookId, setActiveBookIdRaw] = useState<string | null>(null);
  const [chapters, setChapters] = useState<ChapterData[]>([]);
  const [activeChapterId, setActiveChapterIdRaw] = useState<string | null>(null);
  const [chapterCardIds, setChapterCardIds] = useState<string[]>([]);

  // Batch draft generation state
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number; chapterTitle: string } | null>(null);
  const cancelBatchRef = useRef(false);

  // Debounce timer for outline saves
  const outlineSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingOutlineRef = useRef<{ chapterId: string; state: OutlineStateDataElectron | null } | null>(null);

  const activeBook = books.find(b => b.id === activeBookId) ?? null;
  const activeChapter = chapters.find(c => c.id === activeChapterId) ?? null;

  // ─────────────────────────────────────────────────────────────────────
  // BOOK OPERATIONS
  // ─────────────────────────────────────────────────────────────────────

  const refreshBooks = useCallback(async () => {
    if (!activeProjectId || !window.electronBook) {
      setBooks([]);
      return;
    }
    try {
      const list = await window.electronBook.listByProject(activeProjectId);
      setBooks(list);
    } catch {
      setBooks([]);
    }
  }, [activeProjectId]);

  // Refresh books when project changes
  useEffect(() => {
    setActiveBookIdRaw(null);
    setChapters([]);
    setActiveChapterIdRaw(null);
    setChapterCardIds([]);
    refreshBooks();
  }, [refreshBooks]);

  const setActiveBook = useCallback((id: string | null) => {
    setActiveBookIdRaw(id);
    setActiveChapterIdRaw(null);
    setChapterCardIds([]);
  }, []);

  // Refresh chapters when active book changes
  useEffect(() => {
    if (!activeBookId || !window.electronBook) {
      setChapters([]);
      return;
    }
    window.electronBook.get(activeBookId).then(book => {
      if (book) {
        setChapters(book.chapters);
      } else {
        setChapters([]);
      }
    }).catch(() => setChapters([]));
  }, [activeBookId]);

  const createBook = useCallback(async (title: string, description?: string) => {
    if (!activeProjectId || !window.electronBook) return null;
    const book = await window.electronBook.create(activeProjectId, title, description);
    setBooks(prev => [book, ...prev]);
    setActiveBook(book.id);
    return book;
  }, [activeProjectId, setActiveBook]);

  const updateBook = useCallback(async (id: string, update: { title?: string; description?: string }) => {
    if (!window.electronBook) return;
    const updated = await window.electronBook.update(id, update);
    if (updated) {
      setBooks(prev => prev.map(b => b.id === id ? updated : b));
    }
  }, []);

  const deleteBook = useCallback(async (id: string) => {
    if (!window.electronBook) return;
    await window.electronBook.delete(id);
    setBooks(prev => prev.filter(b => b.id !== id));
    if (activeBookId === id) {
      setActiveBook(null);
    }
  }, [activeBookId, setActiveBook]);

  // ─────────────────────────────────────────────────────────────────────
  // CHAPTER OPERATIONS
  // ─────────────────────────────────────────────────────────────────────

  const setActiveChapter = useCallback((id: string | null) => {
    setActiveChapterIdRaw(id);
    if (!id) setChapterCardIds([]);
  }, []);

  const createChapter = useCallback(async (title: string, bookId?: string) => {
    const targetBookId = bookId ?? activeBookId;
    if (!targetBookId || !window.electronBook) return null;
    const chapter = await window.electronBook.createChapter(targetBookId, title);
    setChapters(prev => [...prev, chapter]);
    // Also update the books array so ProjectBrowser sees the chapter
    setBooks(prev => prev.map(b =>
      b.id === targetBookId
        ? { ...b, chapters: [...(b.chapters || []), chapter] }
        : b
    ));
    setActiveChapter(chapter.id);
    return chapter;
  }, [activeBookId, setActiveChapter]);

  const updateChapter = useCallback(async (id: string, update: { title?: string; content?: string; position?: number; wordCount?: number; metadata?: Record<string, unknown> }) => {
    if (!window.electronBook) return;
    const updated = await window.electronBook.updateChapter(id, update);
    if (updated) {
      setChapters(prev => prev.map(c => c.id === id ? updated : c));
      // Sync to books array for ProjectBrowser
      setBooks(prev => prev.map(b => ({
        ...b,
        chapters: (b.chapters || []).map(c => c.id === id ? updated : c),
      })));
    }
  }, []);

  const deleteChapter = useCallback(async (id: string) => {
    if (!window.electronBook) return;
    await window.electronBook.deleteChapter(id);
    setChapters(prev => prev.filter(c => c.id !== id));
    // Sync to books array for ProjectBrowser
    setBooks(prev => prev.map(b => ({
      ...b,
      chapters: (b.chapters || []).filter(c => c.id !== id),
    })));
    if (activeChapterId === id) {
      setActiveChapter(null);
    }
  }, [activeChapterId, setActiveChapter]);

  // Book planning: create a book with N chapters and assign cards to each
  const planAndCreateBook = useCallback(async (plan: {
    title: string;
    description?: string;
    chapters: BookPlanChapter[];
    cardBufferIds: string[];
  }): Promise<BookData | null> => {
    if (!activeProjectId || !window.electronBook) return null;

    // Create the book
    const book = await window.electronBook.create(activeProjectId, plan.title, plan.description);
    setBooks(prev => [book, ...prev]);
    setActiveBookIdRaw(book.id);

    // Create chapters in order, assign cards
    const createdChapters: ChapterData[] = [];
    for (let i = 0; i < plan.chapters.length; i++) {
      const ch = plan.chapters[i];
      const chapterMeta: Record<string, unknown> = {};
      if (ch.theme) chapterMeta.theme = ch.theme;
      if (ch.summary) chapterMeta.summary = ch.summary;
      if (ch.strongestObjection) chapterMeta.strongestObjection = ch.strongestObjection;
      if (ch.howChapterAnswers) chapterMeta.howChapterAnswers = ch.howChapterAnswers;
      if (ch.whatWouldBeLostIfRemoved) chapterMeta.whatWouldBeLostIfRemoved = ch.whatWouldBeLostIfRemoved;
      const chapter = await window.electronBook.createChapter(book.id, ch.title, i, chapterMeta);
      createdChapters.push(chapter);

      // Assign cards from plan indices
      for (let j = 0; j < ch.cardIndices.length; j++) {
        const cardIndex = ch.cardIndices[j];
        const bufferId = plan.cardBufferIds[cardIndex];
        if (bufferId) {
          try {
            await window.electronBook.addCard(chapter.id, bufferId, j);
          } catch (err) {
            log.warn(`Failed to assign card ${cardIndex} to chapter ${chapter.id}:`, err);
          }
        }
      }
    }

    // Update local state
    const updatedBook = { ...book, chapters: createdChapters };
    setBooks(prev => prev.map(b => b.id === book.id ? updatedBook : b));
    setChapters(createdChapters);

    // Auto-select first chapter
    if (createdChapters.length > 0) {
      setActiveChapterIdRaw(createdChapters[0].id);
    }

    return updatedBook;
  }, [activeProjectId]);

  // Add chapters to an existing (empty) book
  const addChaptersToBook = useCallback(async (bookId: string, plan: {
    chapters: BookPlanChapter[];
    cardBufferIds: string[];
  }): Promise<BookData | null> => {
    if (!window.electronBook) return null;

    const createdChapters: ChapterData[] = [];
    for (let i = 0; i < plan.chapters.length; i++) {
      const ch = plan.chapters[i];
      const chapterMeta: Record<string, unknown> = {};
      if (ch.theme) chapterMeta.theme = ch.theme;
      if (ch.summary) chapterMeta.summary = ch.summary;
      if (ch.strongestObjection) chapterMeta.strongestObjection = ch.strongestObjection;
      if (ch.howChapterAnswers) chapterMeta.howChapterAnswers = ch.howChapterAnswers;
      if (ch.whatWouldBeLostIfRemoved) chapterMeta.whatWouldBeLostIfRemoved = ch.whatWouldBeLostIfRemoved;
      const chapter = await window.electronBook.createChapter(bookId, ch.title, i, chapterMeta);
      createdChapters.push(chapter);

      for (let j = 0; j < ch.cardIndices.length; j++) {
        const cardIndex = ch.cardIndices[j];
        const bufferId = plan.cardBufferIds[cardIndex];
        if (bufferId) {
          try {
            await window.electronBook.addCard(chapter.id, bufferId, j);
          } catch (err) {
            log.warn(`Failed to assign card ${cardIndex} to chapter ${chapter.id}:`, err);
          }
        }
      }
    }

    // Update local state
    setBooks(prev => prev.map(b =>
      b.id === bookId ? { ...b, chapters: createdChapters } : b
    ));
    setChapters(createdChapters);

    if (createdChapters.length > 0) {
      setActiveChapterIdRaw(createdChapters[0].id);
    }

    return books.find(b => b.id === bookId)
      ? { ...books.find(b => b.id === bookId)!, chapters: createdChapters }
      : null;
  }, [books]);

  // Stash the current book (rename to "stash") and create a fresh replacement
  const stashBook = useCallback(async (bookId: string): Promise<BookData | null> => {
    if (!activeProjectId || !window.electronBook) return null;

    const book = books.find(b => b.id === bookId);
    if (!book) return null;

    const originalTitle = book.title;
    const originalDescription = book.description;

    // Rename the existing book to "(stash)"
    const stashed = await window.electronBook.update(bookId, {
      title: `${originalTitle} (stash)`,
    });
    if (stashed) {
      setBooks(prev => prev.map(b => b.id === bookId ? stashed : b));
    }

    // Create a fresh empty book with the original title
    const newBook = await window.electronBook.create(activeProjectId, originalTitle, originalDescription);
    setBooks(prev => [newBook, ...prev]);
    setActiveBookIdRaw(newBook.id);
    setChapters([]);
    setActiveChapterIdRaw(null);
    setChapterCardIds([]);

    return newBook;
  }, [activeProjectId, books]);

  const reorderChapters = useCallback(async (orderedChapterIds: string[]) => {
    if (!activeBookId || !window.electronBook?.reorderChapters) return;
    const updatedBook = await window.electronBook.reorderChapters(activeBookId, orderedChapterIds);
    if (updatedBook) {
      setChapters(updatedBook.chapters);
      setBooks(prev => prev.map(b => b.id === activeBookId ? updatedBook : b));
    }
  }, [activeBookId]);

  // ─────────────────────────────────────────────────────────────────────
  // CHAPTER WORKSPACE
  // ─────────────────────────────────────────────────────────────────────

  const hydrateChapter = useCallback(async (chapterId: string) => {
    if (!window.electronBook) return null;
    const result = await window.electronBook.hydrate(chapterId);
    if (result) {
      setChapterCardIds(result.cardBufferIds);
    }
    return result;
  }, []);

  const saveChapterOutline = useCallback(async (state: OutlineStateDataElectron | null) => {
    if (!activeChapterId || !window.electronBook) return;
    // Track pending save for flush-on-unmount
    pendingOutlineRef.current = { chapterId: activeChapterId, state };
    // Debounce: cancel pending save, schedule new one
    if (outlineSaveTimerRef.current) clearTimeout(outlineSaveTimerRef.current);
    outlineSaveTimerRef.current = setTimeout(async () => {
      try {
        await window.electronBook!.saveOutline(activeChapterId, state);
        pendingOutlineRef.current = null;
      } catch (err) {
        log.warn('Outline save failed:', err);
      }
    }, 1000);
  }, [activeChapterId]);

  const saveChapterDraftBuffer = useCallback(async (draftBufferId: string | null, chapterId?: string) => {
    const targetChapterId = chapterId ?? activeChapterId;
    if (!targetChapterId || !window.electronBook) return;
    await window.electronBook.setDraftBuffer(targetChapterId, draftBufferId);
    // Sync draft status to chapters + books array for sidebar display
    const updater = (c: ChapterData) =>
      c.id === targetChapterId ? { ...c, draftBufferId: draftBufferId ?? undefined } : c;
    setChapters(prev => prev.map(updater));
    setBooks(prev => prev.map(b => ({
      ...b,
      chapters: (b.chapters || []).map(updater),
    })));
  }, [activeChapterId]);

  const addCardToChapter = useCallback(async (bufferId: string, sortOrder?: number, chapterId?: string) => {
    const targetChapterId = chapterId ?? activeChapterId;
    if (!targetChapterId || !window.electronBook) return;
    await window.electronBook.addCard(targetChapterId, bufferId, sortOrder);
    setChapterCardIds(prev => prev.includes(bufferId) ? prev : [...prev, bufferId]);
  }, [activeChapterId]);

  const removeCardFromChapter = useCallback(async (bufferId: string) => {
    if (!activeChapterId || !window.electronBook) return;
    await window.electronBook.removeCard(activeChapterId, bufferId);
    setChapterCardIds(prev => prev.filter(id => id !== bufferId));
  }, [activeChapterId]);

  const reorderChapterCards = useCallback(async (orderedBufferIds: string[]) => {
    if (!activeChapterId || !window.electronBook) return;
    await window.electronBook.reorderCards(activeChapterId, orderedBufferIds);
    setChapterCardIds(orderedBufferIds);
  }, [activeChapterId]);

  // ─────────────────────────────────────────────────────────────────────
  // BATCH DRAFT GENERATION
  // ─────────────────────────────────────────────────────────────────────

  const generateAllDrafts = useCallback(async (bookId: string) => {
    if (!window.electronBook || !window.electronBuffer?.generateOutline || !window.electronBuffer?.generateDraft || !window.electronBuffer?.createFromText) return;
    const book = books.find(b => b.id === bookId);
    if (!book) return;

    // Find chapters without drafts
    const chaptersNeedingDrafts = book.chapters.filter(ch => !ch.draftBufferId);
    if (chaptersNeedingDrafts.length === 0) return;

    cancelBatchRef.current = false;
    setBatchGenerating(true);
    const total = chaptersNeedingDrafts.length;

    // Book-awareness: cross-chapter accumulators
    const conceptRegistry: string[] = [];
    const phraseCorpus: string[] = [];
    const chapterSummaries: Array<{ title: string; summary: string }> = [];
    let chapterHooks: string[] = [];

    for (let i = 0; i < chaptersNeedingDrafts.length; i++) {
      if (cancelBatchRef.current) break;
      const ch = chaptersNeedingDrafts[i];
      setBatchProgress({ current: i + 1, total, chapterTitle: ch.title });

      try {
        // 1. Hydrate chapter to get card buffer IDs
        const hydration = await window.electronBook.hydrate(ch.id);
        if (!hydration || hydration.cardBufferIds.length === 0) {
          log.warn(`Batch: skipping chapter "${ch.title}" — no cards`);
          continue;
        }

        // 2. Load card buffers
        const cardResults = await Promise.allSettled(
          hydration.cardBufferIds.map(id => window.electronBuffer!.load(id))
        );
        const cards: Array<{ title: string; excerpt: string; text: string }> = [];
        for (const r of cardResults) {
          if (r.status === 'fulfilled' && r.value) {
            const title = (r.value.origin?.metadata?.title as string) ?? r.value.text.split('\n')[0]?.slice(0, 100) ?? '';
            cards.push({ title, excerpt: r.value.text.slice(0, 500), text: r.value.text });
          }
        }
        if (cards.length === 0) continue;

        if (cancelBatchRef.current) break;

        // 3. Generate outline (use saved outline if it exists, otherwise auto-generate)
        let sections: Array<{ title: string; cardRefs: number[]; notes: string }>;
        if (hydration.outlineState?.sections && hydration.outlineState.sections.length > 0) {
          sections = hydration.outlineState.sections;
        } else {
          const chapterMeta = ch.metadata as Record<string, string> | undefined;
          const outlineResult = await window.electronBuffer.generateOutline(
            cards.map(c => ({ title: c.title, excerpt: c.excerpt })),
            undefined,
            {
              bookTitle: book.title,
              chapterTitle: ch.title,
              chapterTheme: chapterMeta?.theme,
              chapterSummary: chapterMeta?.summary,
              siblingChapterTitles: book.chapters.map(c => c.title),
              chapterPosition: ch.position,
              totalChapters: book.chapters.length,
              priorChapterSummaries: chapterSummaries.length > 0 ? [...chapterSummaries] : undefined,
              priorConceptRegistry: conceptRegistry.length > 0 ? conceptRegistry.slice(0, 50) : undefined,
              chapterObligation: chapterMeta?.strongestObjection ? {
                strongestObjection: chapterMeta.strongestObjection,
                howChapterAnswers: chapterMeta.howChapterAnswers ?? '',
                whatWouldBeLostIfRemoved: chapterMeta.whatWouldBeLostIfRemoved ?? '',
              } : undefined,
            }
          );
          sections = outlineResult.sections;
          // Save the generated outline to the chapter
          await window.electronBook.saveOutline(ch.id, { sections });
        }

        if (cancelBatchRef.current) break;

        // 4. Generate draft
        const siblingTitles = book.chapters.map(c => c.title);
        const chapterMeta2 = ch.metadata as Record<string, string> | undefined;
        const draftResult = await window.electronBuffer.generateDraft({
          sections,
          cardTexts: cards.map(c => c.text),
          targetWordCount: 1500,
          arcType: 'narrative',
          bookContext: {
            bookTitle: book.title,
            bookDescription: book.description,
            chapterTitle: ch.title,
            chapterTheme: chapterMeta2?.theme,
            chapterSummary: chapterMeta2?.summary,
            chapterPosition: ch.position,
            totalChapters: book.chapters.length,
            siblingChapterTitles: siblingTitles,
            priorConceptRegistry: conceptRegistry.length > 0 ? [...conceptRegistry] : undefined,
            priorPhraseCorpus: phraseCorpus.length > 0 ? [...phraseCorpus] : undefined,
            priorChapterSummaries: chapterSummaries.length > 0 ? [...chapterSummaries] : undefined,
            chapterObligation: chapterMeta2?.strongestObjection ? {
              strongestObjection: chapterMeta2.strongestObjection,
              howChapterAnswers: chapterMeta2.howChapterAnswers ?? '',
              whatWouldBeLostIfRemoved: chapterMeta2.whatWouldBeLostIfRemoved ?? '',
            } : undefined,
            priorChapterHooks: chapterHooks.length > 0 ? [...chapterHooks] : undefined,
          },
        });

        // Accumulate cross-chapter insights for next iteration
        if (draftResult.extractedInsights) {
          conceptRegistry.push(...draftResult.extractedInsights.concepts);
          phraseCorpus.push(...draftResult.extractedInsights.phrases);
          // Replace (not append) — only the most recent chapter's hooks matter
          if (draftResult.extractedInsights.hooksPlanted?.length) {
            chapterHooks = draftResult.extractedInsights.hooksPlanted;
          } else {
            chapterHooks = [];
          }
        }

        // Accumulate chapter summary from draft text (~first 300 words)
        const draftWords = draftResult.draft.split(/\s+/).filter(Boolean);
        chapterSummaries.push({
          title: ch.title,
          summary: draftWords.slice(0, 300).join(' '),
        });

        if (cancelBatchRef.current) break;

        // 5. Create draft buffer and link to chapter
        const draftBuffer = await window.electronBuffer.createFromText(draftResult.draft, {
          initialState: 'staged',
          metadata: {
            generatedDraft: true,
            title: `Draft: ${ch.title}`,
            draftGenerationMode: 'normal',
            generationModel: draftResult.generationModel,
          },
        });

        await window.electronBook.setDraftBuffer(ch.id, draftBuffer.id);

        // 6. Update word count on chapter
        const wordCount = draftResult.draft.split(/\s+/).filter(Boolean).length;
        await window.electronBook.updateChapter(ch.id, { wordCount });

        // 7. Update local state so UI reflects immediately
        const chapterUpdater = (c: ChapterData) =>
          c.id === ch.id ? { ...c, draftBufferId: draftBuffer.id, wordCount } : c;
        setChapters(prev => prev.map(chapterUpdater));
        setBooks(prev => prev.map(b => ({
          ...b,
          chapters: (b.chapters || []).map(chapterUpdater),
        })));

        log.info(`Batch: draft created for chapter "${ch.title}" → ${draftBuffer.id}`);
      } catch (err) {
        log.warn(`Batch: draft generation failed for chapter "${ch.title}":`, err);
        // Continue with next chapter
      }
    }

    setBatchGenerating(false);
    setBatchProgress(null);
  }, [books]);

  const cancelBatchGeneration = useCallback(() => {
    cancelBatchRef.current = true;
  }, []);

  // Cleanup debounce timer on unmount — flush pending outline save
  useEffect(() => {
    return () => {
      if (outlineSaveTimerRef.current) clearTimeout(outlineSaveTimerRef.current);
      const pending = pendingOutlineRef.current;
      if (pending && window.electronBook) {
        window.electronBook.saveOutline(pending.chapterId, pending.state).catch(() => {});
      }
    };
  }, []);

  const value = useMemo<BookContextValue>(() => ({
    books,
    activeBook,
    activeBookId,
    setActiveBook,
    createBook,
    updateBook,
    deleteBook,
    refreshBooks,
    chapters,
    activeChapter,
    activeChapterId,
    setActiveChapter,
    createChapter,
    updateChapter,
    deleteChapter,
    planAndCreateBook,
    addChaptersToBook,
    stashBook,
    reorderChapters,
    chapterCardIds,
    hydrateChapter,
    saveChapterOutline,
    saveChapterDraftBuffer,
    addCardToChapter,
    removeCardFromChapter,
    reorderChapterCards,
    batchGenerating,
    batchProgress,
    generateAllDrafts,
    cancelBatchGeneration,
  }), [
    books, activeBook, activeBookId, setActiveBook, createBook, updateBook, deleteBook, refreshBooks,
    chapters, activeChapter, activeChapterId, setActiveChapter, createChapter, updateChapter, deleteChapter,
    planAndCreateBook, addChaptersToBook, stashBook, reorderChapters,
    chapterCardIds, hydrateChapter, saveChapterOutline, saveChapterDraftBuffer,
    addCardToChapter, removeCardFromChapter, reorderChapterCards,
    batchGenerating, batchProgress, generateAllDrafts, cancelBatchGeneration,
  ]);

  return (
    <BookContext.Provider value={value}>
      {children}
    </BookContext.Provider>
  );
}

export function useBook(): BookContextValue {
  const ctx = useContext(BookContext);
  if (!ctx) throw new Error('useBook must be used within BookProvider');
  return ctx;
}
